import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import * as Sentry from '@sentry/nextjs';
import { getCustomerNavigationHref } from 'components/account/impersonation';
import { requestZeusFromBrowser } from 'components/api/zeusBrowserClient';
import { pushUrl, replaceUrl } from 'components/helpers/history';
import type {
  MarketplaceBrowseVariables,
  MarketplaceMapMode,
} from 'components/marketplace/browse/marketplaceBrowse';
import { MarketplaceViewportAreaNameDocument } from 'components/marketplace/map/__generated__/viewport-area-name.zeus';
import {
  getViewportAreaName,
  getViewportAreaNameInput,
  getViewportSamplePoints,
  getViewportSpanKm,
} from 'components/marketplace/map/viewportAreaName';
import { useRouter } from 'next/router';

import type { GeoPoint, MapLocation, MarketInfo, MarketShape } from '../map/types';
import { getViewportFromShape } from '../map/utils';
import type { SavedSearchFieldsFragment } from '../savedSearches/__generated__/saved-searches.zeus';
import { buildSaveSearchQuery, toPropertySearchQuery } from '../savedSearches/mapping';
import { canApplySavedSearchInPlace, pickPathname } from '../savedSearches/routing';
import { resolveSavedSearchArea } from '../savedSearches/savedSearchArea';
import { useSavedSearches } from '../savedSearches/useSavedSearches';
import type { ActiveMarket } from '../types';
import { DEFAULT_LISTING_SORT_SLUG } from './sortOptions';
import { type SearchStatus, useMarketplaceSearch } from './useMarketplaceSearch';
import {
  buildQueryParams,
  isUuid,
  normalizeSearchQuery,
  normalizeSearchVertices,
  parseQueryParamsFromSearch,
  SAVED_SEARCH_ID_PARAM,
  updateQueryParams,
} from './useSearchQueries';
import type { PropertySearchQuery } from './useSearchQueries';
import type { useSearchState } from './useSearchState';
import { buildPayloadFromQuery } from './useSearchState';

type SearchState = ReturnType<typeof useSearchState>;
type SearchFilterOverride = Omit<PropertySearchQuery, 'market' | 'vertices' | 'zoneIds'>;

interface UseMarketplaceSearchControllerArgs {
  initialVariables: MarketplaceBrowseVariables;
  initialSearchPathname: string;
  activeMarkets: ActiveMarket[];
  searchState: SearchState;
  marketInfo: MarketInfo;
  initialVertices?: GeoPoint[] | null;
  initialZoneIds?: string[] | null;
  initialViewportAreaName?: string | null;
  resolveViewportAreaName?: (vertices: GeoPoint[], signal: AbortSignal) => Promise<string | null>;
  /** `ssid` from the URL: the saved search this page view was applied from. */
  initialSavedSearchId?: string | null;
  /** Map projection to request; the Google map asks for adaptive clusters. */
  mapMode?: MarketplaceMapMode;
  /** Polygon adaptive mode uses when the search area is a market or zone. */
  mapViewport?: GeoPoint[];
}

const resolveViewportAreaNameFromZeus = async (
  vertices: GeoPoint[],
  signal: AbortSignal,
): Promise<string | null> => {
  const spanKm = getViewportSpanKm(vertices);
  const responses = await Promise.all(
    getViewportSamplePoints(vertices).map((point) =>
      requestZeusFromBrowser(
        MarketplaceViewportAreaNameDocument,
        { input: getViewportAreaNameInput(point, spanKm) },
        signal,
      ),
    ),
  );
  return getViewportAreaName(responses, spanKm);
};

const getShownUrl = () => `${window.location.pathname}${window.location.search}`;

export const useMarketplaceSearchController = ({
  initialVariables,
  initialSearchPathname,
  activeMarkets,
  searchState,
  marketInfo: initialMarketInfo,
  initialVertices,
  initialZoneIds,
  initialViewportAreaName,
  resolveViewportAreaName = resolveViewportAreaNameFromZeus,
  initialSavedSearchId,
  mapMode = 'markers',
  mapViewport,
}: UseMarketplaceSearchControllerArgs) => {
  const router = useRouter();
  const normalizedInitialVertices = normalizeSearchVertices(initialVertices);
  const [searchRoute, setSearchRoute] = useState<{
    pathname: string;
    marketInfo: MarketInfo;
    /**
     * The market a saved search stores for this route, when it differs from
     * the route's identifier: a zone route has none.
     */
    saveMarket?: string | null;
  }>({
    pathname: initialSearchPathname,
    marketInfo: initialMarketInfo,
  });
  const { pathname: searchPathname, marketInfo } = searchRoute;
  // A route keeps the page's map polygon until it brings an area of its own.
  const activeMapViewport = useMemo(() => {
    if (marketInfo.shape === initialMarketInfo.shape) return mapViewport;
    return mapMode !== 'markers' ? getViewportFromShape(marketInfo.shape) : undefined;
  }, [marketInfo.shape, initialMarketInfo.shape, mapViewport, mapMode]);
  // Each search the visitor starts takes a number, so a saved search whose
  // area lookup finishes after a newer search knows it lost.
  const searchGenerationRef = useRef(0);
  /** The number of the latest search the visitor started; each new one counts up. */
  const getSearchGeneration = useCallback(() => searchGenerationRef.current, []);
  // The saved search an on-page apply is loading once its area shows: from
  // then until its results apply, the area is ahead of the filters, so the
  // page must not save or update a search from that mix.
  // Kept with its search number, so only that apply can end it.
  const [applying, setApplying] = useState<{
    savedSearch: SavedSearchFieldsFragment;
    generation: number;
  } | null>(null);
  const applyingSavedSearch = applying?.savedSearch ?? null;
  /** Starts a search: it takes the next number and ends any saved search apply. */
  const beginSearch = useCallback(() => {
    setApplying(null);
    searchGenerationRef.current += 1;
    return searchGenerationRef.current;
  }, []);
  const {
    hasMore,
    prefetchAfter,
    isLoadingMore,
    loadMoreError,
    loadMore,
    appliedListInput,
    mapMarkers,
    mapClusters,
    listings,
    appliedSort,
    appliedAvailableSoon,
    appliedIncludeAvailableSoon,
    appliedPage,
    isLoading,
    hasError,
    errorId,
    clearError,
    runSearch: runListSearch,
  } = useMarketplaceSearch(initialVariables, searchState.get.sort, mapMode, activeMapViewport);
  // The URL of the history entry an on-page saved search added before its
  // results showed. A new destination that starts while that entry is current
  // reuses it, so Back never returns to an area that never showed results.
  // Results that show, or any other address, end it.
  const provisionalUrlRef = useRef<string | null>(null);
  const runSearch = useCallback(
    async (...args: Parameters<typeof runListSearch>): Promise<SearchStatus> => {
      const status = await runListSearch(...args);
      if (status === 'applied') provisionalUrlRef.current = null;
      return status;
    },
    [runListSearch],
  );
  /** Whether the current history entry is a saved search's early one. */
  const isOnProvisionalEntry = useCallback(
    () => provisionalUrlRef.current !== null && provisionalUrlRef.current === getShownUrl(),
    [],
  );
  /**
   * Adds a history entry for a new destination. `reuseEntry` comes from
   * `isOnProvisionalEntry` when the search started.
   */
  const pushDestinationUrl = useCallback((url: string, reuseEntry: boolean) => {
    if (reuseEntry || url === getShownUrl()) {
      replaceUrl(url);
    } else {
      pushUrl(url);
    }
    provisionalUrlRef.current = null;
  }, []);

  // Keeping "no polygon" as null lets save-search distinguish market-level
  // searches from user-drawn areas.
  const [drawnVertices, setDrawnVertices] = useState<GeoPoint[] | null>(normalizedInitialVertices);
  const getLocationRoute = useCallback(
    (location: MapLocation) => {
      // A city name alone can match a market in a different state.
      const market =
        location.zoneType === 'CITY'
          ? activeMarkets.find(
              (candidate) =>
                `${candidate.displayName}, ${candidate.displayState}` === location.label,
            )
          : undefined;
      return {
        pathname: market?.homesPath ?? `/homes/zid_${location.value}`,
        marketInfo: {
          ...initialMarketInfo,
          identifier: market?.identifier ?? null,
          name: market?.identifier ?? location.label,
          displayName: market?.displayName ?? location.label,
        },
      };
    },
    [activeMarkets, initialMarketInfo],
  );
  const [selectedLocation, setSelectedLocation] = useState<MapLocation>();
  const [viewportAreaName, setViewportAreaName] = useState<string | null>(() =>
    normalizedInitialVertices ? (initialViewportAreaName ?? 'this area') : null,
  );
  const locationLabel =
    selectedLocation?.label || viewportAreaName || marketInfo.displayName || 'you';
  const isMountedRef = useRef(true);
  const areaNameAbortControllerRef = useRef<AbortController | null>(null);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      areaNameAbortControllerRef.current?.abort();
      areaNameAbortControllerRef.current = null;
    };
  }, []);
  // Vertices win over zones (the same precedence used during SSR). When the page
  // hydrates with a viewport polygon, ignore route zone ids so client searches
  // and saves keep the polygon instead of silently expanding to the whole zone.
  const [routeZoneIds, setRouteZoneIds] = useState<string[] | null>(() => {
    if (normalizedInitialVertices) {
      return null;
    }
    return initialZoneIds?.length ? initialZoneIds : null;
  });

  // Resolve the active id against the shared cache so updates and deletions
  // propagate from every saved-search surface.
  const { savedSearches, hasLoaded: savedSearchesLoaded } = useSavedSearches();
  const [activeSavedSearchId, setActiveSavedSearchId] = useState<string | null>(
    initialSavedSearchId ?? null,
  );
  const activeSavedSearch = useMemo(
    () => savedSearches.find((saved) => saved.id === activeSavedSearchId) ?? null,
    [savedSearches, activeSavedSearchId],
  );

  const markActiveSavedSearch = useCallback((savedSearchId: string) => {
    if (!savedSearchId) {
      return;
    }
    setActiveSavedSearchId(savedSearchId);
    const params = new URLSearchParams(window.location.search);
    params.set(SAVED_SEARCH_ID_PARAM, savedSearchId);
    replaceUrl(`${window.location.pathname}?${params.toString()}`);
  }, []);

  const clearActiveSavedSearch = useCallback(() => {
    setActiveSavedSearchId(null);
    const params = new URLSearchParams(window.location.search);
    if (!params.has(SAVED_SEARCH_ID_PARAM)) {
      return;
    }
    params.delete(SAVED_SEARCH_ID_PARAM);
    replaceUrl(`${window.location.pathname}?${params.toString()}`);
  }, []);

  useEffect(() => {
    // Only reconcile against a genuinely loaded list; transient empties during
    // auth churn must not wipe the active reference.
    if (
      savedSearchesLoaded &&
      activeSavedSearchId &&
      !savedSearches.some((saved) => saved.id === activeSavedSearchId)
    ) {
      // oxlint-disable-next-line react/set-state-in-effect -- react19-baseline: pre-existing, do not copy. Derive during render or set from the event instead
      clearActiveSavedSearch();
    }
  }, [savedSearchesLoaded, savedSearches, activeSavedSearchId, clearActiveSavedSearch]);

  // The location the next search runs against. `buildPayloadFromQuery` treats
  // these as mutually exclusive (vertices > zoneIds > market); zone selection
  // clears `vertices` so a chosen zone wins, otherwise the current map polygon
  // (or, when empty, the bare market) drives results.
  const activeZoneIds = useMemo(() => {
    if (selectedLocation?.value) {
      return [selectedLocation.value];
    }
    return routeZoneIds?.length ? routeZoneIds : null;
    // oxlint-disable-next-line react/preserve-manual-memoization -- react19-baseline: pre-existing, do not copy. Fix the compiler errors inside the callback first
  }, [routeZoneIds, selectedLocation?.value]);

  const currentSearchLocation: Pick<PropertySearchQuery, 'market' | 'zoneIds' | 'vertices'> =
    useMemo(
      () => ({
        market: marketInfo.name,
        vertices: activeZoneIds?.length ? null : drawnVertices,
        zoneIds: activeZoneIds,
      }),
      [marketInfo.name, drawnVertices, activeZoneIds],
    );

  // Runs a search against the current location, optionally overriding filter
  // values being set in the same tick (the async filter setters wouldn't be
  // visible to `buildSearchPayload` yet). Backs both the quick and full filter
  // menus.
  const applyFilters = useCallback(
    async (filterOverride: SearchFilterOverride = {}): Promise<SearchStatus> => {
      beginSearch();
      const status = await runSearch(
        searchState.buildSearchPayload({ ...filterOverride, ...currentSearchLocation }),
        filterOverride.sort ?? searchState.get.sort,
      );
      if (status !== 'applied') {
        return status;
      }
      const canonicalQuery = { ...searchState.getCanonical(), ...filterOverride };
      if (activeZoneIds?.length === 1) {
        const route = selectedLocation ? getLocationRoute(selectedLocation) : searchRoute;
        const params = buildQueryParams({
          ...canonicalQuery,
          vertices: null,
          zoneIds: route.marketInfo.identifier ? activeZoneIds : null,
        });
        replaceUrl(`${route.pathname}?${params.toString()}`);
        setSearchRoute(route);
        return status;
      }
      updateQueryParams(canonicalQuery);
      return status;
    },
    [
      searchState,
      runSearch,
      currentSearchLocation,
      activeZoneIds,
      selectedLocation,
      getLocationRoute,
      searchRoute,
      beginSearch,
    ],
  );

  // Unlike a filter refinement, a page is its own destination: it lands as a new
  // history entry so Back returns to the page the visitor came from.
  const applyPage = useCallback(
    async (page: number): Promise<SearchStatus> => {
      beginSearch();
      const reuseEntry = isOnProvisionalEntry();
      const status = await runSearch(
        searchState.buildSearchPayload({ ...currentSearchLocation, page }),
        searchState.get.sort,
      );
      if (status !== 'applied') {
        return status;
      }
      // Canonical keys from the URL plus the new page. Passing them in is what
      // makes the alias-stripping loop fire, so `?minBeds=3` becomes
      // `?bedrooms=3` and the pushed address matches the anchor's href — while
      // seeding still preserves params neither path models, such as `ssid`.
      const params = buildQueryParams({
        ...parseQueryParamsFromSearch(window.location.search),
        page,
      });
      const query = params.toString();
      pushDestinationUrl(
        query ? `${window.location.pathname}?${query}` : window.location.pathname,
        reuseEntry,
      );
      return status;
    },
    [
      searchState,
      runSearch,
      currentSearchLocation,
      isOnProvisionalEntry,
      pushDestinationUrl,
      beginSearch,
    ],
  );

  // In-place searches do not update Next's route props. Block its competing
  // history fetch so it cannot hydrate stale props over the restored search.
  useEffect(() => {
    router.beforePopState(() => false);
    const handlePopState = () => {
      // Rebuilt from the entry's own URL rather than live filter state, which
      // has moved on: a Back that restores `bedrooms=3` must search with it, not
      // with whatever the filter panel holds now. Parsed the same way SSR parses
      // it, so a malformed or repeated param resolves identically on both paths.
      const query = parseQueryParamsFromSearch(window.location.search);
      const zoneIds =
        query.zoneIds ??
        (searchPathname.startsWith('/homes/zid_')
          ? [searchPathname.slice('/homes/zid_'.length)]
          : null);
      // A different location needs its SSR metadata and map camera, not just new listings.
      if (
        window.location.pathname !== searchPathname ||
        JSON.stringify(normalizeSearchVertices(query.vertices)) !== JSON.stringify(drawnVertices) ||
        (!drawnVertices && JSON.stringify(zoneIds) !== JSON.stringify(activeZoneIds))
      ) {
        window.location.reload();
        return;
      }
      provisionalUrlRef.current = null;
      beginSearch();
      void (async () => {
        const status = await runSearch(
          buildPayloadFromQuery({ ...query, ...currentSearchLocation }, currentSearchLocation),
          query.sort ?? DEFAULT_LISTING_SORT_SLUG,
        );
        if (status === 'applied') {
          searchState.applyCanonical(query);
          searchState.set.setSort(query.sort ?? DEFAULT_LISTING_SORT_SLUG);
          setActiveSavedSearchId(
            new URLSearchParams(window.location.search).get(SAVED_SEARCH_ID_PARAM),
          );
        }
      })();
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      router.beforePopState(() => true);
    };
  }, [
    router,
    runSearch,
    searchState,
    currentSearchLocation,
    searchPathname,
    drawnVertices,
    activeZoneIds,
    beginSearch,
  ]);

  // Preserve an explicit dropdown selection for retry, but commit its route
  // only after the corresponding results apply.
  const applyZoneSearch = useCallback(
    async (location: MapLocation) => {
      const zoneId = location.value;
      if (!isUuid(zoneId)) {
        return;
      }
      beginSearch();

      areaNameAbortControllerRef.current?.abort();
      areaNameAbortControllerRef.current = null;
      setViewportAreaName(null);
      setSelectedLocation(location);
      setRouteZoneIds([zoneId]);
      setDrawnVertices(null);
      const reuseEntry = isOnProvisionalEntry();

      const status = await runSearch(
        searchState.buildSearchPayload({ zoneIds: [zoneId], vertices: null }),
        searchState.get.sort,
      );
      if (status !== 'applied') {
        return;
      }

      const route = getLocationRoute(location);
      const params = buildQueryParams(
        { ...searchState.getCanonical(), zoneIds: route.marketInfo.identifier ? [zoneId] : null },
        { seedFromUrl: false },
      );
      pushDestinationUrl(`${route.pathname}?${params.toString()}`, reuseEntry);
      setSearchRoute(route);
      setActiveSavedSearchId(null);
    },
    [
      searchState,
      runSearch,
      getLocationRoute,
      isOnProvisionalEntry,
      pushDestinationUrl,
      beginSearch,
    ],
  );

  const applyViewport = useCallback(
    async (
      coordinates: GeoPoint[],
      location?: MapLocation,
      {
        commitAfter,
        onSearched,
      }: {
        /** Shows the results only once this resolves true; see `runSearch`. */
        commitAfter?: Promise<boolean>;
        /** Reports the search's status as soon as it is known, before the area name. */
        onSearched?: (status: SearchStatus) => void;
      } = {},
    ) => {
      const vertices = normalizeSearchVertices(coordinates);
      if (!vertices || (location && !isUuid(location.value))) {
        return;
      }
      const generation = beginSearch();
      const reuseEntry = isOnProvisionalEntry();
      areaNameAbortControllerRef.current?.abort();
      const areaNameAbortController = new AbortController();
      areaNameAbortControllerRef.current = areaNameAbortController;
      // The area becomes the location once, with its results or at the landing.
      // A search that fails or loses first never moves it.
      // Typed by assertion: the closures below change it, so it must not narrow.
      let areaState = 'pending' as 'pending' | 'committed' | 'dropped';
      const commitArea = () => {
        if (areaState !== 'pending') return;
        areaState = 'committed';
        const route = location ? getLocationRoute(location) : searchRoute;
        const params = buildQueryParams(
          { ...searchState.getCanonical(), vertices, page: null, market: null },
          { seedFromUrl: !location },
        );
        const url = `${route.pathname}?${params.toString()}`;
        if (location) {
          pushDestinationUrl(url, reuseEntry);
          setActiveSavedSearchId(null);
        } else {
          replaceUrl(url);
        }
        setSearchRoute(route);

        setDrawnVertices(vertices);
        setViewportAreaName(location?.label ?? locationLabel);
        setSelectedLocation(undefined);
        setRouteZoneIds(null);
      };
      // A search held until the camera lands makes the landed area the
      // location at the landing, so a filter, sort or page search the visitor
      // starts before its results arrive searches that area too.
      void commitAfter?.then((landed) => {
        if (landed && isMountedRef.current && generation === searchGenerationRef.current) {
          commitArea();
        }
      });
      const areaNamePromise = resolveViewportAreaName(
        vertices,
        areaNameAbortController.signal,
      ).catch((error: unknown) => {
        if (!areaNameAbortController.signal.aborted) {
          Sentry.captureException(error, {
            tags: { feature: 'marketplace-browse', operation: 'viewportAreaName' },
          });
        }
        return null;
      });
      const status = await runSearch(
        searchState.buildSearchPayload({ vertices }),
        searchState.get.sort,
        { commitAfter },
      );
      onSearched?.(status);
      // Commit the viewport only with its results so URL, list, and subsequent
      // saved-search/load-more actions stay coherent.
      if (status === 'applied' && isMountedRef.current) {
        commitArea();
      } else if (areaState !== 'committed' || !isMountedRef.current) {
        areaState = 'dropped';
        areaNameAbortController.abort();
        if (areaNameAbortControllerRef.current === areaNameAbortController) {
          areaNameAbortControllerRef.current = null;
        }
        return;
      }
      // An area made the location at the landing keeps its name lookup, also
      // when a filter search replaced its own search.

      const areaName = await areaNamePromise;
      if (
        areaNameAbortControllerRef.current === areaNameAbortController &&
        !areaNameAbortController.signal.aborted
      ) {
        setViewportAreaName(areaName ?? 'this area');
        areaNameAbortControllerRef.current = null;
      }
    },
    [
      searchState,
      runSearch,
      searchRoute,
      getLocationRoute,
      resolveViewportAreaName,
      locationLabel,
      isOnProvisionalEntry,
      pushDestinationUrl,
      beginSearch,
    ],
  );

  // Zone, polygon, and cross-market searches need fresh SSR chrome; only a
  // same-market market-level search can apply in place safely.
  const applySavedSearch = useCallback(
    async (savedSearch: SavedSearchFieldsFragment) => {
      // Saved searches store filters and location only, so the active sort
      // carries over instead of snapping back to the default.
      const query = normalizeSearchQuery({
        ...toPropertySearchQuery(savedSearch),
        sort: searchState.get.sort,
      });
      beginSearch();

      if (!canApplySavedSearchInPlace(savedSearch, marketInfo.identifier)) {
        const params = buildQueryParams(query, { seedFromUrl: false });
        params.set(SAVED_SEARCH_ID_PARAM, savedSearch.id);
        window.location.assign(
          getCustomerNavigationHref(`${pickPathname(savedSearch)}?${params.toString()}`),
        );
        return;
      }

      // State setters are not visible to `buildSearchPayload` in this tick.
      const status = await runSearch(
        buildPayloadFromQuery(query, { market: marketInfo.name }),
        // normalizeSearchQuery nulls the default slug, so fall back to state.
        query.sort ?? searchState.get.sort,
      );
      if (status !== 'applied') {
        return;
      }

      searchState.applyCanonical(query);
      areaNameAbortControllerRef.current?.abort();
      areaNameAbortControllerRef.current = null;
      setViewportAreaName(null);
      setSelectedLocation(undefined);
      // Only market-level searches apply in place, so the applied state has no
      // drawn polygon.
      setDrawnVertices(null);
      setRouteZoneIds(null);
      setActiveSavedSearchId(savedSearch.id);

      // A zone pathname would re-SSR the zone search on reload/share/back.
      const params = buildQueryParams(query, { seedFromUrl: false });
      params.set(SAVED_SEARCH_ID_PARAM, savedSearch.id);
      const pathname = `/homes/${marketInfo.identifier}`;
      replaceUrl(`${pathname}?${params.toString()}`);
      setSearchRoute({ pathname, marketInfo });
    },
    [marketInfo, searchState, runSearch, beginSearch],
  );

  const savedSearchAreaAbortRef = useRef<AbortController | null>(null);
  useEffect(() => () => savedSearchAreaAbortRef.current?.abort(), []);

  // The map page applies a saved search where the visitor is: it searches the
  // saved area with the saved filters and shows the area with `onArea`, which
  // runs before the results arrive so the map can move while they load. Only an
  // area the page cannot find, or an inactive market, still loads a new page.
  const applySavedSearchOnPage = useCallback(
    async (
      savedSearch: SavedSearchFieldsFragment,
      onArea: (shape: MarketShape) => void,
    ): Promise<SearchStatus | 'navigated'> => {
      // Saved searches store filters and location only, so the active sort
      // carries over instead of snapping back to the default.
      const query = normalizeSearchQuery({
        ...toPropertySearchQuery(savedSearch),
        sort: searchState.get.sort,
      });
      const generation = beginSearch();
      savedSearchAreaAbortRef.current?.abort();
      const abortController = new AbortController();
      savedSearchAreaAbortRef.current = abortController;
      let area: Awaited<ReturnType<typeof resolveSavedSearchArea>> = null;
      try {
        area = await resolveSavedSearchArea(savedSearch, abortController.signal);
      } catch (error) {
        if (abortController.signal.aborted) return 'superseded';
        Sentry.captureException(error, {
          tags: { feature: 'marketplace-browse', operation: 'savedSearchArea' },
        });
      }
      // A search the visitor started during the lookup wins.
      if (
        abortController.signal.aborted ||
        !isMountedRef.current ||
        generation !== searchGenerationRef.current
      ) {
        return 'superseded';
      }
      savedSearchAreaAbortRef.current = null;
      if (!area) {
        const params = buildQueryParams(query, { seedFromUrl: false });
        params.set(SAVED_SEARCH_ID_PARAM, savedSearch.id);
        window.location.assign(
          getCustomerNavigationHref(`${pickPathname(savedSearch)}?${params.toString()}`),
        );
        return 'navigated';
      }

      onArea(area.marketInfo.shape);
      setApplying({ savedSearch, generation });
      // The map now shows the saved area, so it is the location from here on:
      // a filter change during the search searches it too. A drawn area wins
      // over zones, as in server rendering.
      const vertices = normalizeSearchVertices(query.vertices);
      areaNameAbortControllerRef.current?.abort();
      areaNameAbortControllerRef.current = null;
      setViewportAreaName(vertices ? 'this area' : null);
      setSelectedLocation(undefined);
      setDrawnVertices(vertices);
      setRouteZoneIds(!vertices && query.zoneIds?.length ? query.zoneIds : null);
      setSearchRoute({
        pathname: area.pathname,
        marketInfo: area.marketInfo,
        saveMarket: area.saveMarket,
      });
      // The address moves to the saved area now too, with the filters still in
      // use, so a filter or page search during this one keeps the area. A saved
      // search is a new destination, so Back returns to the earlier search;
      // applying the one already shown adds no entry.
      const params = buildQueryParams(query, { seedFromUrl: false });
      params.set(SAVED_SEARCH_ID_PARAM, savedSearch.id);
      const url = `${area.pathname}?${params.toString()}`;
      // The early entry this saved search added, if it added one.
      let ownUrl: string | null = null;
      if (url !== getShownUrl()) {
        const areaParams = buildQueryParams(
          {
            ...searchState.getCanonical(),
            vertices,
            zoneIds: vertices ? null : (query.zoneIds ?? null),
          },
          { seedFromUrl: false },
        );
        const areaUrl = `${area.pathname}?${areaParams.toString()}`;
        if (areaUrl !== getShownUrl()) {
          pushDestinationUrl(areaUrl, isOnProvisionalEntry());
          provisionalUrlRef.current = areaUrl;
          ownUrl = areaUrl;
        }
      }
      const status = await runSearch(
        // The saved location, with the same precedence: a drawn area, zones, the market.
        buildPayloadFromQuery(query, {
          market: area.marketInfo.name,
          zoneIds: vertices ? null : (query.zoneIds ?? null),
          vertices,
        }),
        // normalizeSearchQuery nulls the default slug, so fall back to state.
        query.sort ?? searchState.get.sort,
        // The map polygon the next render adopts for this route, so the
        // results stay cached instead of loading again.
        {
          mapViewport:
            mapMode !== 'markers' ? getViewportFromShape(area.marketInfo.shape) : undefined,
        },
      );
      // A newer search already ended this apply; otherwise it ends here.
      setApplying((current) => (current?.generation === generation ? null : current));
      if (status !== 'applied' || !isMountedRef.current) {
        return status;
      }

      searchState.applyCanonical(query);
      setActiveSavedSearchId(savedSearch.id);
      pushDestinationUrl(url, ownUrl !== null && ownUrl === getShownUrl());

      if (vertices) {
        const areaNameAbortController = new AbortController();
        areaNameAbortControllerRef.current = areaNameAbortController;
        void resolveViewportAreaName(vertices, areaNameAbortController.signal)
          .catch(() => null)
          .then((areaName) => {
            if (
              areaNameAbortControllerRef.current === areaNameAbortController &&
              !areaNameAbortController.signal.aborted
            ) {
              setViewportAreaName(areaName ?? 'this area');
              areaNameAbortControllerRef.current = null;
            }
          });
      }
      return status;
    },
    [
      searchState,
      runSearch,
      mapMode,
      resolveViewportAreaName,
      isOnProvisionalEntry,
      pushDestinationUrl,
      beginSearch,
    ],
  );

  // The canonical query handed to the save-search button. `getCanonical()`
  // omits location, so the active market/zone/polygon is merged back in here.
  // We persist the routing slug (`identifier`), never the display `name`, so a
  // zone page's label isn't written into the saved search and later routed to a
  // non-existent `/homes/<label>`. Only a genuinely drawn polygon is persisted
  // as vertices — with none, the search saves as a clean market-level record.
  const getSaveSearchQuery = useCallback(
    (): PropertySearchQuery =>
      buildSaveSearchQuery({
        canonical: searchState.getCanonical(),
        marketIdentifier:
          searchRoute.saveMarket === undefined ? marketInfo.identifier : searchRoute.saveMarket,
        selectedZoneId: selectedLocation?.value,
        routeZoneIds,
        vertices: activeZoneIds?.length ? null : drawnVertices,
      }),
    [
      searchState,
      marketInfo.identifier,
      searchRoute.saveMarket,
      selectedLocation?.value,
      routeZoneIds,
      activeZoneIds,
      drawnVertices,
    ],
  );

  return {
    hasMore,
    prefetchAfter,
    isLoadingMore,
    loadMoreError,
    loadMore,
    appliedListInput,
    mapMarkers,
    mapClusters,
    listings,
    appliedSort,
    appliedAvailableSoon,
    appliedIncludeAvailableSoon,
    appliedPage,
    isLoading,
    hasError,
    errorId,
    clearError,
    selectedLocation,
    setSelectedLocation,
    applyFilters,
    applyZoneSearch,
    applyViewport,
    applyPage,
    applySavedSearch,
    applySavedSearchOnPage,
    getSaveSearchQuery,
    activeSavedSearch,
    markActiveSavedSearch,
    clearActiveSavedSearch,
    locationLabel,
    searchPathname,
    marketInfo,
    searchLocation: currentSearchLocation,
    getSearchGeneration,
    applyingSavedSearch,
  };
};
